import React, { useEffect, useMemo, useState } from "react";
import { useACPP, GRADE_ORDER } from "../ACPPContext";
import {
  ModuleCard, ModuleHeader, SectionDivider, SoftPanel, MetricCard, InfoBanner,
  Tag, BtnRow, BtnPrimary, BtnLight, HelpText,
} from "../components/ui";

export default function Module3GradesOffered() {
  const {
    selectedGrades,
    module3Loading,
    module3LoadError,
    canEditModules1to5,
    loadModule3Grades,
    saveModule3Grades,
    resetModule3Grades,
  } = useACPP();
  const [localSelected, setLocalSelected] = useState([]);

  useEffect(() => {
    setLocalSelected(selectedGrades);
  }, [selectedGrades]);

  const selectedSorted = useMemo(
    () => GRADE_ORDER.filter((g) => selectedGrades.includes(g)),
    [selectedGrades]
  );
  const localSorted = useMemo(
    () => GRADE_ORDER.filter((g) => localSelected.includes(g)),
    [localSelected]
  );
  const hasUnsavedChanges = localSorted.join("|") !== selectedSorted.join("|");
  const status = !selectedSorted.length && !localSorted.length
    ? "Pending"
    : hasUnsavedChanges
      ? "In Progress"
      : "Saved";

  const saveGrades = async () => {
    if (!canEditModules1to5) return;
    if (!localSelected.length) { alert("Please select at least one grade."); return; }
    await saveModule3Grades(localSelected);
    alert("Grades saved successfully.");
  };

  const resetGrades = async () => {
    if (!canEditModules1to5) return;
    setLocalSelected([]);
    await resetModule3Grades();
  };

  const orderedSelected = localSorted;
  const gradeCount = orderedSelected.length;
  const schoolSpan = gradeCount >= 2
    ? `${orderedSelected[0]} to ${orderedSelected[gradeCount - 1]}`
    : gradeCount === 1 ? orderedSelected[0] : "-";
  const readiness = gradeCount ? "Active" : "Pending";

  return (
    <ModuleCard id="module3">
      <section className="mb-6">
        <div className="text-center px-3 py-2">
          <h3 className="text-4xl font-bold text-[#D67C2C] mb-3">Grades Offered</h3>
          <p className="text-[#1D3469] text-lg font-medium max-w-4xl mx-auto leading-[1.45]">
            Select the exact school grade structure for this ACPP year. All downstream modules use this selection.
          </p>
        </div>
      </section>

      <ModuleHeader
        step="Module 3"
        title="Grades Offered by the School"
        description="Select grades quickly. This will control all further modules."
        showStatus={false}
      />

   
      {module3LoadError ? (
        <HelpText className="text-[#b42318]">{module3LoadError}</HelpText>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <MetricCard label="Grades Selected" value={gradeCount} meta="Total active grades" />
        <MetricCard label="School Span" value={schoolSpan} meta="Range of grades" />
        <MetricCard label="Planning Readiness" value={readiness} meta="Controls further modules" />
        <MetricCard label="Status" value={status} meta="Current save state" />
      </div>


     <InfoBanner>
        This module is saved per <strong>active ACPP academic year</strong>. Create or activate an ACPP plan year to maintain separate grade structures across years.
      </InfoBanner>
      <SectionDivider />


      <div className="grid grid-cols-1 xl:grid-cols-[1fr_1.2fr] gap-6">
        {canEditModules1to5 ? (
          <SoftPanel title="Select Grades for Active Year">
            <div className="rounded-xl border border-[#d9e4f0] bg-white p-4">
              <details className="group">
                <summary className="list-none cursor-pointer flex items-center justify-between text-sm font-semibold text-[#1D3469]">
                  <span>{localSorted.length ? `${localSorted.length} grade(s) selected` : "Choose grades"}</span>
                  <span className="text-[#6B7280] group-open:rotate-180 transition-transform">⌄</span>
                </summary>
                <div className="mt-4">
                  <div className="flex flex-wrap gap-2 mb-3">
                    <button
                      type="button"
                      onClick={() => setLocalSelected([...GRADE_ORDER])}
                      className="px-3 py-1.5 rounded-lg border border-gray-300 text-xs text-[#374151] hover:bg-gray-50 transition-colors"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={() => setLocalSelected([])}
                      className="px-3 py-1.5 rounded-lg border border-gray-300 text-xs text-[#374151] hover:bg-gray-50 transition-colors"
                    >
                      Clear
                    </button>
                  </div>

                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 max-h-[260px] overflow-auto pr-1">
                    {GRADE_ORDER.map(g => {
                      const checked = localSelected.includes(g);
                      return (
                        <label
                          key={g}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm cursor-pointer transition-colors ${
                            checked
                              ? "bg-[#eef6fc] border-[#cfe3f3] text-[#1D3469]"
                              : "bg-white border-gray-200 text-[#374151] hover:bg-gray-50"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setLocalSelected(prev => [...prev, g]);
                              } else {
                                setLocalSelected(prev => prev.filter(x => x !== g));
                              }
                            }}
                            className="accent-[#D67C2C]"
                          />
                          <span>{g}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </details>
            </div>
            <HelpText>Expandable checklist with quick select/clear actions.</HelpText>
          </SoftPanel>
        ) : (
          <SoftPanel title="Selection Locked">
            <p className="text-sm text-[#6B7280] leading-[1.8] m-0">
              Grades are visible in read-only mode for your panel.
            </p>
          </SoftPanel>
        )}

        <SoftPanel title="Current Year Grade Map">
          <div className="flex flex-wrap gap-2 mb-4">
            {orderedSelected.length
              ? orderedSelected.map(g => <Tag key={g}>{g}</Tag>)
              : <Tag color="yellow">No grades selected yet</Tag>
            }
          </div>
          <div className="bg-white border border-[#d9e4f0] rounded-xl p-4">
            <div className="text-sm text-[#6B7280] mb-2">Status Summary</div>
            <div className="text-base font-semibold text-[#1D3469]">
              {status === "Saved" ? "Saved for active ACPP year" : status === "In Progress" ? "Changes pending save" : "Waiting for grade selection"}
            </div>
          </div>
        </SoftPanel>
      </div>

      {canEditModules1to5 ? (
        <BtnRow>
          <BtnLight onClick={loadModule3Grades}>Refresh Year Data</BtnLight>
          <BtnPrimary onClick={saveGrades} disabled={module3Loading || !canEditModules1to5}>Save Grades</BtnPrimary>
        </BtnRow>
      ) : null}
    </ModuleCard>
  );
}
